'use client';

import { useEffect, useRef, useState } from 'react';

/** Constante de tempo da aproximação ao valor recebido: a cada `EASE_TAU_MS`
 * a barra cobre ~63% da distância que lhe falta ao alvo. */
const EASE_TAU_MS = 180;

/** Tempo sem uma mudança REAL do valor a partir do qual a barra começa a
 * rastejar por conta própria. Bem acima do heartbeat de 0.5s do backend (ver
 * `HeartbeatProgressSender`) para o rastejo só entrar nas zonas em que
 * ninguém está a emitir progresso — o download dos blobs, a conversão
 * LibreOffice, uma página em OCR, o intervalo dos 90 aos 100 — e não a
 * competir com mensagens que estão mesmo a chegar. */
const CREEP_DELAY_MS = 1500;

/** Constante de tempo do rastejo: desacelera à medida que se aproxima do
 * teto, pelo que a barra dá sinal de vida sem nunca parecer estar a correr. */
const CREEP_TAU_MS = 9000;

/** Quanto a barra se pode adiantar ao último valor confirmado pelo backend,
 * em pontos percentuais. */
const CREEP_MAX_POINTS = 3;

/** O rastejo nunca chega aos 100: o fim é sempre o backend a confirmá-lo. */
const CREEP_CEILING = 99;

/** Queda no valor recebido a partir da qual se assume um processo novo (ou um
 * rebaseline) em vez de ruído, e a barra acompanha para baixo. Dentro do
 * mesmo processo o valor é monótono por construção — ver
 * `ProjectRepository.bump_indexing_progress` e o `Math.max` de
 * `questionnaireRunStore`. */
const RESET_DROP_POINTS = 2;

/** Distância ao alvo abaixo da qual se considera já alcançado. */
const SNAP_EPSILON = 0.05;

/** Teto do delta de tempo por frame: sem isto, voltar a um separador que
 * esteve em segundo plano (onde o rAF não corre) daria um salto único
 * proporcional a todo o tempo ausente. */
const MAX_FRAME_MS = 250;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    setPrefers(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setPrefers(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return prefers;
}

/**
 * Converte a sequência de percentagens discretas que chega por websocket num
 * valor animado, frame a frame.
 *
 * Faz duas coisas distintas:
 *
 * 1. **Interpola** até ao último valor recebido, em vez de saltar para ele —
 *    as fronteiras de fase da indexação são degraus secos (5 → 10 → 60 → 65,
 *    ver `RAGSystemDocumentLoader`), e os incrementos por página/batch chegam
 *    em rajadas.
 * 2. **Rasteja**, de forma limitada, quando o backend fica em silêncio: as
 *    fases mais lentas da pipeline são precisamente as que não emitem
 *    progresso nenhum, e uma barra parada durante minutos lê-se como uma
 *    indexação encravada. Limitado a `CREEP_MAX_POINTS` acima do valor
 *    confirmado e a `CREEP_CEILING` em absoluto.
 *
 * O valor devolvido nunca desce, exceto num recomeço (queda maior que
 * `RESET_DROP_POINTS`): se o rastejo se adiantou ao backend, a barra fica
 * onde está até o valor real a ultrapassar, em vez de recuar.
 */
export function useSmoothProgress(target: number, active = true): number {
  const clampedTarget = Math.min(100, Math.max(0, Number.isFinite(target) ? target : 0));
  const prefersReducedMotion = usePrefersReducedMotion();

  const [displayed, setDisplayed] = useState(clampedTarget);
  const displayedRef = useRef(clampedTarget);
  const targetRef = useRef(clampedTarget);
  /** Instante do último valor NOVO — a base do atraso do rastejo. O heartbeat
   * reenvia a mesma percentagem a cada 0.5s, e esses reenvios não contam como
   * progresso: o efeito abaixo só corre quando o número muda mesmo. */
  const changedAtRef = useRef(0);

  useEffect(() => {
    targetRef.current = clampedTarget;
    changedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (clampedTarget < displayedRef.current - RESET_DROP_POINTS) {
      displayedRef.current = clampedTarget;
      setDisplayed(clampedTarget);
    }
  }, [clampedTarget]);

  useEffect(() => {
    if (!active || prefersReducedMotion) {
      // Sem animação: a barra passa a acompanhar o valor recebido tal e qual
      // (e sem rastejo, que é movimento que ninguém pediu).
      if (displayedRef.current !== targetRef.current) {
        displayedRef.current = targetRef.current;
        setDisplayed(targetRef.current);
      }
      return;
    }

    let frame = 0;
    let last = performance.now();

    // O loop mantém-se a correr enquanto o processo estiver ativo, mesmo
    // depois de a barra assentar, porque é ele que acorda para o rastejo
    // quando o backend emudece. Sem alterações a fazer não há `setState`,
    // pelo que uma barra parada não custa re-renders.
    const step = (now: number) => {
      const dt = Math.min(now - last, MAX_FRAME_MS);
      last = now;

      const goal = targetRef.current;
      let value = displayedRef.current;

      if (goal > value + SNAP_EPSILON) {
        value += (goal - value) * (1 - Math.exp(-dt / EASE_TAU_MS));
      } else {
        // Já alcançou o alvo (ou adiantou-se-lhe por rastejo): nunca recua.
        value = Math.max(value, goal);
        const ceiling = Math.min(goal + CREEP_MAX_POINTS, CREEP_CEILING);
        if (now - changedAtRef.current >= CREEP_DELAY_MS && ceiling > value) {
          value += (ceiling - value) * (1 - Math.exp(-dt / CREEP_TAU_MS));
        }
      }

      if (Math.abs(value - displayedRef.current) > 0.001) {
        displayedRef.current = value;
        setDisplayed(value);
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [active, prefersReducedMotion]);

  return displayed;
}
